import {
    React,
    useState, useParams, useEffect, useApi, Dialog, 
    GridColDef, GridRowSelectionModel, IconButton, 
    parseMonthYearToUTCDate,
    Toaster,
    MenuItem,
    TextField,
    Button,
    Checkbox,
    ListItemText,
    Box,
    Typography,
    CloseRoundedIcon,
    useNavigate,
} from '../../../utils/globalImports';

import { ApiResponse } from '../../../services/apiService';
import { invoiceTypes } from '../../Invoicing/InvoiceTypesMapping';
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

interface ModalProps {
    open: boolean;
    onClose: () => void;
    displayType:number;
}

interface Invoice {
    monthYear: string;
    invoiceAmount: number;
    hasInvoiced?: boolean;
}

const ProjectInvoice: React.FC<ModalProps> = ({ open, onClose,displayType }) => {

    const { execute } = useApi();
    const [rows, setRows] = useState<any[]>([]);
    const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
    const [toast, setToast] = useState({ open: false, message: "", type: "success" });
    const { projectId } = useParams<{ projectId?: string }>(); // Get projectId from URL
    const userId = Number(localStorage.getItem("userId"));
    const role = localStorage.getItem("role");
    const [invoiceType, setInvoiceType] = useState<number[]>([]);
    const [selectedInvoiceTypes, setSelectedInvoiceTypes] = useState<
        { [key: number]: number[] }[]
    >([]);

    const navigate = useNavigate();
    useEffect(() => {
        if (open) {
            fetchData();
        }
    }, [open]);

    // fetch Invoice Data 
    const fetchData = async () => {
        const { data } = await execute<Invoice[]>('get', `/Project/GetMonthWiseInvoice/${projectId}/${displayType}`);
        if (data) {
            setRows(data);
            setSelectionModel(data.filter(r => r.hasInvoiced).map(r => r.monthYear));
        }
    };

    const columns: GridColDef<(typeof rows)[number]>[] = [
        {
            field: 'monthYear', headerName: 'Month', flex: 1,
        },
        {
            field: 'invoiceAmount', headerName: 'Amount', flex: 1, headerAlign: 'left',
            align: 'left',
            type: 'number',
            valueFormatter: (params: any) => {
                const value = params;

                if (value === null || value === undefined || isNaN(Number(value))) {
                    return '';
                }

                return Number(value).toLocaleString('en-GB', {
                    style: 'currency',
                    currency: 'GBP',
                });
            },
        },
    ];

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        const payload = rows
            .filter((row) =>
                selectionModel.includes(row.monthYear)
                // && !row.hasInvoiced
                // && Number(row.invoiceAmount) > 0
            )
            .map((row) => ({
                projectID: Number(projectId),
                invoiceDate: parseMonthYearToUTCDate(row.monthYear),
                invoiceAmount: Number(row.invoiceAmount),
                createdid: Number(userId),
                displayType:Number(displayType),
            }));

        if (payload.length > 0) {
            const response: ApiResponse<any> = await execute('post', '/Project/SaveProjectInvoice', payload);

            if (!response) return;

            if (response.error) {
                setToast({ open: true, message: "Please try again.", type: "error" });
            } else {
                setToast({ open: true, message: response.data.message, type: "success" });
                onClose()
            }
        }
        else {
            setToast({ open: true, message: "No month has been selected.", type: "warning" });
        }

    }

    const handleInvoiceTypeChange = (projectId: number, value: number[]) => {

        setSelectedInvoiceTypes((prev) => {

            const index = prev.findIndex(obj => Object.keys(obj)[0] === projectId.toString());

            if (index !== -1) {
                const updated = [...prev];
                updated[index] = { [projectId]: value };
                return updated;
            }

            return [...prev, { [projectId]: value }];
        });

    };

    useEffect(() => {
        handleInvoiceTypeChange(Number(projectId), invoiceType);
    }, [invoiceType]);

    const handleNext = () => {
        if (invoiceType.length === 0) {
            setToast({ open: true, message: "Please select at least one invoice type.", type: "warning" });
            return;
        } else {
            navigate("/invoicing/create-invoice", {
                state: { selectedInvoiceTypes: selectedInvoiceTypes, forecast: true }
            })
        }
    }
    return (
        <Dialog
        open={open}
        onClose={(event, reason) => {
            if (reason !== "backdropClick") {
                onClose();
                setInvoiceType([]);
            }
        }}
    maxWidth="md"
    fullWidth
    PaperProps={{
        sx: {
            borderRadius: "18px",
            overflow: "hidden",
        },
    }}
>
    {/* Header */}
    <Box
        sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            px: 4,
            py: 3,
            borderBottom: "1px solid #E5E7EB",
        }}
    >
        <Box display="flex" gap={2}>
            {/* Icon */}
            <Box
                sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "50%",
                    background: "#EEF4FF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <ReceiptLongOutlinedIcon
                    sx={{
                        color: "#2563EB",
                        fontSize: 28,
                    }}
                />
            </Box>

            {/* Title */}
            <Box>
                <Typography
                    sx={{
                        fontSize: "34px",
                        fontWeight: 700,
                        color: "#0F172A",
                        lineHeight: 1.2,
                    }}
                >
                    Add Invoice
                </Typography>

                <Typography
                    sx={{
                        mt: 0.5,
                        fontSize: "18px",
                        color: "#64748B",
                        fontWeight: 400,
                    }}
                >
                    Select the invoice type to continue.
                </Typography>
            </Box>
        </Box>

        {/* Close Button */}
        <IconButton
            onClick={() => {
                onClose();
                setInvoiceType([]);
            }}
            sx={{
                border: "1px solid #D6DCE5",
                borderRadius: "12px",
                width: 48,
                height: 48,
                color: "#2563EB",

                "&:hover": {
                    background: "#F8FAFC",
                },
            }}
        >
            <CloseRoundedIcon />
        </IconButton>
    </Box>

    {/* Body */}
    <Box
        sx={{
            px: 4,
            py: 4,
            minHeight: "300px",
        }}
    >
        <Typography
            sx={{
                mb: 1.2,
                fontSize: "18px",
                fontWeight: 600,
                color: "#0F172A",
            }}
        >
            Invoice Type
            <Box
                component="span"
                sx={{ color: "#EF4444", ml: 0.3 }}
            >
                *
            </Box>
        </Typography>

        <TextField
            select
            fullWidth
            placeholder="Select invoice type"
            SelectProps={{
                displayEmpty: true,
                multiple: true,
                value: invoiceType,
                onChange: (e) =>
                    setInvoiceType(
                        typeof e.target.value === "string"
                            ? e.target.value.split(",").map(Number)
                            : (e.target.value as number[])
                    ),

                renderValue: (selected) => {
                    if ((selected as number[]).length === 0) {
                        return (
                            <Typography
                                sx={{
                                    color: "#94A3B8",
                                    fontSize: "16px",
                                }}
                            >
                                Select invoice type
                            </Typography>
                        );
                    }

                    return invoiceTypes
                        .filter((type) =>
                            (selected as number[]).includes(type.id)
                        )
                        .map((type) => type.label)
                        .join(", ");
                },

                MenuProps: {
                    PaperProps: {
                        sx: {
                            borderRadius: "12px",
                            mt: 1,
                            maxHeight: 300,
                        },
                    },
                },
            }}
            sx={{
                "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    minHeight: "58px",
                    fontSize: "16px",

                    "& fieldset": {
                        borderColor: "#3B82F6",
                    },

                    "&:hover fieldset": {
                        borderColor: "#2563EB",
                    },

                    "&.Mui-focused fieldset": {
                        borderWidth: "2px",
                        borderColor: "#2563EB",
                    },
                },
            }}
        >
            {invoiceTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                    <Checkbox
                        checked={invoiceType.includes(type.id)}
                    />

                    <ListItemText primary={type.label} />
                </MenuItem>
            ))}
        </TextField>
    </Box>

    {/* Footer */}
    <Box
        sx={{
            borderTop: "1px solid #E5E7EB",
            px: 4,
            py: 3,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
        }}
    >
        <Button
            variant="outlined"
            onClick={() => {
                onClose();
                setInvoiceType([]);
            }}
            sx={{
                width: "170px",
                height: "52px",
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "18px",
                borderColor: "#3B82F6",
                color: "#2563EB",

                "&:hover": {
                    borderColor: "#2563EB",
                    background: "#F8FAFC",
                },
            }}
        >
            Cancel
        </Button>

        <Button
            variant="contained"
            onClick={handleNext}
            endIcon={<ArrowForwardIcon />}
            sx={{
                width: "180px",
                height: "52px",
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "18px",
                background:
                    "linear-gradient(180deg, #2563EB 0%, #1D4ED8 100%)",
                boxShadow: "none",

                "&:hover": {
                    background:
                        "linear-gradient(180deg, #1D4ED8 0%, #1E40AF 100%)",
                    boxShadow: "none",
                },
            }}
        >
            Next
        </Button>
    </Box>

    <Toaster
        open={toast.open}
        message={toast.message}
        type={toast.type as "success" | "error" | "warning"}
        onClose={() =>
            setToast({
                ...toast,
                open: false,
            })
        }
    />
</Dialog>
    );
};

export default ProjectInvoice;
